import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  CloseButton,
  Container,
  ContainerContent,
  Content,
  FilterButton,
  FilterContainer,
  Footer,
  FooterRow,
  InfiniteScrollMarker,
  LeftContent,
  LeftContentScroll,
  ListContainer,
  ListContainerV2,
  ListItem,
  ListItemColumn,
  ListItemRow,
  LoaderContainer,
  LoaderMoreContainer,
  MediaCountRow,
  MediaCountText,
  MediaWrapper,
  NotValidMedia,
  PlayIconWrapper,
  RightContent,
  SelectIconWrapper,
  SoldMedia,
  StyledImage,
  Title,
  UnselectText,
} from './SelectMediaModal.styles';
import { ALBUM_MEDIA_TYPE, MEDIA_ASSET_SALE_TAG } from '../../../utils/constants';
import { fetchAssets } from '../../../store/slices/user/asyncThunks';
import { useDispatch, useSelector } from 'react-redux';
import { selectFetchAssetsInfo } from '../../../store/slices/user/slice';
import Spinner from '../../Spinner/Spinner';
import { ReactComponent as PlayIcon } from '../../../assets/icons/play-white.svg';
import { ReactComponent as CheckCircleEmptyIcon } from '../../../assets/icons/check-circle-empty.svg';
import { ReactComponent as CheckCircleIcon } from '../../../assets/icons/check-circle-broken.svg';
import { ReactComponent as ArrowIcon } from '../../../assets/icons/chevron_right.svg';
import { ReactComponent as ImageIcon } from '../../../assets/icons/image-04.svg';
import { ReactComponent as VideoRecorderIcon } from '../../../assets/icons/video-recorder.svg';
import { Text14Bold, Text14Light, Text16Regular } from '../../utils/Text/Text.styles';
import { Button } from '../../utils/Button/Button';
import BaseModalV2 from '../utils/BaseModalV2/BaseModalV2';
import { selectFetchAssetFoldersInfo } from '../../../store/slices/assetFolder/slice';
import { fetchAssetFolders } from '../../../store/slices/assetFolder/asyncThunks';
import PreviewAlbumMediaModal from '../PreviewAlbumMediaModal/PreviewAlbumMediaModal';

const FILTERS = [
  { title: 'All', value: null },
  { title: 'Photos', value: ALBUM_MEDIA_TYPE.PHOTO },
  { title: 'Videos', value: ALBUM_MEDIA_TYPE.VIDEO },
];

const SelectMediaModal = ({
  modalRef,
  tag,
  multiple,
  onAdd,
  reload = true,
  onClose = () => {},
  userInfluencerId,
  maxSelectedItems = 100,
  exactMatch,
  influencerId,
}) => {
  const dispatch = useDispatch();

  const [selectedFilter, setSelectedFilter] = useState(FILTERS[0]);
  const [page, setPage] = useState(1);
  const loadMoreRef = useRef(null);
  const [ref, setRef] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(multiple ? [] : null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [previewSelectedMedia, setPreviewSelectedMedia] = useState(null);
  const previewAlbumMediaModalRef = useRef();

  const { data, maxReached, pending, fetchMorePending } = useSelector(selectFetchAssetsInfo);
  const { data: foldersData, pending: foldersPending } = useSelector(selectFetchAssetFoldersInfo);

  useEffect(() => {
    dispatch(fetchAssetFolders({ ...(influencerId && { influencerId }) }));
  }, [dispatch, influencerId]);

  useEffect(() => {
    if (tag.length && reload) {
      setPage(1);
      dispatch(
        fetchAssets({
          page: 1,
          kind: selectedFilter.value,
          tag,
          ...(userInfluencerId && { userInfluencerId }),
          exactMatch,
          ...(selectedFolder && { folderId: selectedFolder }),
          ...(influencerId && { influencerId }),
        }),
      );
    }
  }, [dispatch, selectedFilter, tag, reload, userInfluencerId, exactMatch, selectedFolder, influencerId]);

  useEffect(() => {
    if (multiple) {
      setSelectedMedia([]);
    } else {
      setSelectedMedia(null);
    }
  }, [multiple]);

  const handleIntersection = useCallback(
    entries => {
      const [entry] = entries;
      if (entry.isIntersecting && !pending && !fetchMorePending && !maxReached) {
        setPage(prev => prev + 1);
        dispatch(
          fetchAssets({
            page: page + 1,
            kind: selectedFilter.value,
            tag,
            ...(userInfluencerId && { userInfluencerId }),
            exactMatch,
            ...(selectedFolder && { folderId: selectedFolder }),
            ...(influencerId && { influencerId }),
          }),
        );
      }
    },
    [
      pending,
      maxReached,
      fetchMorePending,
      page,
      dispatch,
      selectedFilter,
      tag,
      userInfluencerId,
      exactMatch,
      selectedFolder,
      influencerId,
    ],
  );

  useEffect(() => {
    if (ref) {
      const observer = new IntersectionObserver(handleIntersection, { root: ref, threshold: 1.0 });
      const target = loadMoreRef.current;
      if (target) observer.observe(target);

      return () => {
        if (target) observer.unobserve(target);
      };
    }
  }, [handleIntersection, ref]);

  const handleRefCallback = node => {
    if (node) {
      setRef(node);
    }
  };

  const selectMedia = media => {
    if (multiple) {
      if (selectedMedia.find(m => m.id === media.id)) {
        setSelectedMedia(selectedMedia.filter(m => m.id !== media.id));
      } else {
        if (selectedMedia.length < maxSelectedItems) {
          setSelectedMedia([...selectedMedia, media]);
        }
      }
    } else {
      if (selectedMedia && selectedMedia.id === media.id) {
        setSelectedMedia(null);
      } else {
        setSelectedMedia(media);
      }
    }
  };

  return (
    <>
      <BaseModalV2
        ref={modalRef}
        onClose={() => {
          modalRef.current.hide();
          onClose();
        }}
        onAfterClose={() => {
          setSelectedMedia(multiple ? [] : null);
        }}>
        <Container>
          <ContainerContent>
            <LeftContent>
              <Title>Vault</Title>
              <LeftContentScroll>
                <ListItem
                  $selected={!selectedFolder}
                  onClick={() => {
                    setSelectedFolder(null);
                  }}>
                  <ListItemColumn>
                    <Text16Regular>All media</Text16Regular>
                  </ListItemColumn>
                  <ArrowIcon color="#1672BA" />
                </ListItem>
                {foldersPending ? (
                  <LoaderContainer>
                    <Spinner width={120} height={120} />
                  </LoaderContainer>
                ) : (
                  <ListContainerV2>
                    {foldersData.map(folder => {
                      return (
                        <ListItem
                          key={folder.id}
                          $selected={selectedFolder === folder.id}
                          onClick={() => {
                            setSelectedFolder(folder.id);
                          }}>
                          <ListItemColumn>
                            <Text16Regular>{folder.name}</Text16Regular>
                            <ListItemRow>
                              <MediaCountRow>
                                <ImageIcon color="#9DA4AE" height={18} width={18} />
                                <MediaCountText>{folder.numOfPhotos || 0}</MediaCountText>
                              </MediaCountRow>
                              <MediaCountRow>
                                <VideoRecorderIcon color="#9DA4AE" height={18} width={18} />
                                <MediaCountText>{folder.numOfVideos || 0}</MediaCountText>
                              </MediaCountRow>
                            </ListItemRow>
                          </ListItemColumn>
                          <ArrowIcon color="#1672BA" />
                        </ListItem>
                      );
                    })}
                  </ListContainerV2>
                )}
              </LeftContentScroll>
            </LeftContent>
            <RightContent>
              <FilterContainer>
                {FILTERS.map(f => {
                  return (
                    <FilterButton
                      $selected={f.value === selectedFilter.value}
                      onClick={() => setSelectedFilter(f)}
                      key={f.value}>
                      {f.title}
                    </FilterButton>
                  );
                })}
              </FilterContainer>
              <Content ref={handleRefCallback}>
                {pending ? (
                  <LoaderContainer>
                    <Spinner width={120} height={120} />
                  </LoaderContainer>
                ) : (
                  <>
                    <ListContainer>
                      {data.map(media => {
                        const isSelected = multiple
                          ? Array.isArray(selectedMedia)
                            ? selectedMedia.find(m => m.id === media.id)
                            : false
                          : selectedMedia?.id === media.id;

                        let notValid;
                        if (tag.includes(MEDIA_ASSET_SALE_TAG.SINGLE_PAID)) {
                          notValid = !media.aiDescription || !media.fileId || !media.category;
                        } else {
                          notValid = !media.fileId || !media.aiDescription;
                        }

                        return (
                          <MediaWrapper
                            key={media.id}
                            onClick={() => {
                              if (!notValid) {
                                setPreviewSelectedMedia(media);
                                previewAlbumMediaModalRef.current.show();
                              }
                            }}>
                            <SelectIconWrapper
                              onClick={e => {
                                if (!notValid) {
                                  selectMedia(media);
                                }
                                e.stopPropagation();
                              }}>
                              {isSelected ? (
                                <CheckCircleIcon color="#fff" width={24} height={24} />
                              ) : (
                                <CheckCircleEmptyIcon color="#fff" width={24} height={24} />
                              )}
                            </SelectIconWrapper>
                            {notValid && (
                              <NotValidMedia>
                                <Text14Light>Processing, please wait...</Text14Light>
                              </NotValidMedia>
                            )}
                            {!media.available && (
                              <SoldMedia $free={media.tags.includes(MEDIA_ASSET_SALE_TAG.SINGLE_FREE)}>
                                {media.tags.includes(MEDIA_ASSET_SALE_TAG.SINGLE_FREE) ? 'SENT' : 'SOLD'}
                              </SoldMedia>
                            )}
                            <StyledImage src={media.thumbnailUrl} />
                            {media.kind === ALBUM_MEDIA_TYPE.VIDEO && (
                              <PlayIconWrapper>
                                <PlayIcon fill="white" />
                              </PlayIconWrapper>
                            )}
                          </MediaWrapper>
                        );
                      })}
                    </ListContainer>
                    {fetchMorePending && (
                      <LoaderMoreContainer>
                        <Spinner width={100} height={100} />
                      </LoaderMoreContainer>
                    )}
                    <InfiniteScrollMarker ref={loadMoreRef} />
                  </>
                )}
              </Content>
            </RightContent>
          </ContainerContent>
          <Footer>
            <CloseButton
              onClick={() => {
                modalRef.current.hide();
                onClose();
              }}>
              Close
            </CloseButton>
            <FooterRow>
              {multiple && Array.isArray(selectedMedia) && !!selectedMedia.length && (
                <>
                  <Text14Bold>Selected {selectedMedia.length}</Text14Bold>
                  <UnselectText onClick={() => setSelectedMedia([])}>Unselect</UnselectText>
                </>
              )}
              <Button
                title="Add"
                disabled={multiple ? (Array.isArray(selectedMedia) ? !selectedMedia.length : true) : !selectedMedia}
                onClick={() => {
                  onAdd(selectedMedia);
                  modalRef.current.hide();
                }}
              />
            </FooterRow>
          </Footer>
        </Container>
      </BaseModalV2>
      <PreviewAlbumMediaModal
        modalRef={previewAlbumMediaModalRef}
        type={previewSelectedMedia?.kind}
        url={previewSelectedMedia?.url}
        setSelectedUrl={setPreviewSelectedMedia}
      />
    </>
  );
};

export default memo(SelectMediaModal);
